import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { CREATE_TINYCMS_PREVIEW, GET_TINYCMS_ARTICLE, GET_TINYCMS_DATASET } from '../../graphql';
import { SitePreviewFrame } from '../cms/SitePreviewFrame';
import { useTinycmsSites } from '../cms/sites';
import type { ApprovalItem, PostView, PreviewSpec } from './approvalQueue';
import { plainBodyHtml } from './approvalWords';

/**
 * The Approvals review's right pane (design §6): the actual proposed content,
 * outlined in the review colour, under a sticky "Not live yet" bar — never a
 * placeholder, so the person never has to leave to see what they approve. An
 * article renders from its working copy; pages, forms and the site's look
 * render as the real site shows the working copy (a preview token, framed as
 * Design does); Reach posts as cards; requests in their own words.
 */

/** Preview links minted this session, per item, so stepping back and forth mints none. */
const minted = new Map<string, string>();

/** The working copy at the page's own address (…/preview/<token>?next=/about → /about?preview=…). */
function workingAddress(previewUrl: string): string {
  const u = new URL(previewUrl);
  const token = u.pathname.split('/preview/')[1] || '';
  const next = u.searchParams.get('next') || '/';
  u.pathname = next;
  u.searchParams.delete('next');
  u.searchParams.set('preview', token);
  return u.toString();
}

const initials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)).toUpperCase() || 'TC';
};

function Bar({
  where,
  pill = 'Not live yet',
  legend = false,
}: {
  where: string;
  pill?: string;
  /** A new site's review: what the dashed outlines mean (design §3.1). */
  legend?: boolean;
}) {
  return (
    <div className="ar-pv-bar" data-legend={legend ? 'true' : undefined}>
      <span className="ar-pv-dot" aria-hidden="true" />
      <span className="ar-pv-where" title={where}>
        {where}
      </span>
      {legend ? (
        <span className="ar-pv-legend">
          <span>
            <i data-tone="filled" aria-hidden="true" />
            Filled in
          </span>
          <span>
            <i aria-hidden="true" />
            Placeholder
          </span>
        </span>
      ) : null}
      <span className="ar-pv-pill">{pill}</span>
    </div>
  );
}

/** The review's outlines on the working copy (smagent-sites PreviewOutline ?outline=starter). */
function withReview(address: string, hash: string) {
  const u = new URL(address);
  u.searchParams.set('outline', 'starter');
  u.searchParams.set('embed', '1');
  // The frame is drawn at desktop width and scaled about in half: tags stay readable.
  u.searchParams.set('ui', '2');
  u.hash = hash.replace(/^#/, '');
  return u.toString();
}

function ArticlePreview({
  articleId,
  host,
  check,
}: {
  articleId: string;
  host: string;
  check: ApprovalItem['check'];
}) {
  const { accountId } = useAccount();
  const { data, loading, error } = useQuery(GET_TINYCMS_ARTICLE, {
    variables: { accountId, id: articleId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const article = data?.tinycmsArticle;
  const c = article?.content || {};
  const body = useMemo(
    () => (c.bodyHtml ? DOMPurify.sanitize(plainBodyHtml(String(c.bodyHtml))) : ''),
    [c.bodyHtml]
  );
  const scheduledAt = article?.state?.scheduled?.at ? new Date(article.state.scheduled.at) : null;
  const where = `${host}/${article?.slug || c.slug || ''} · ${scheduledAt ? 'scheduled page' : 'draft page'}`;
  const altMissing = Boolean(c.hero && !c.hero.alt) || /alt text/i.test(check.text);
  return (
    <>
      <Bar where={where} />
      <div className="ar-pv-body">
        {!article ? (
          <div className="ar-pv-loading">
            {loading ? 'Loading the article…' : error ? 'The article couldn’t load.' : ''}
          </div>
        ) : (
          <article className="ar-article">
            <div className="ar-article-meta">
              {c.tags?.[0] ? (
                <>
                  <span>{String(c.tags[0])}</span>
                  <span>·</span>
                </>
              ) : null}
              <span>
                {scheduledAt
                  ? `Goes out ${scheduledAt.toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`
                  : 'Publishes when you approve'}
              </span>
            </div>
            <h2 className="ar-article-title">{c.title || article.title || 'Untitled'}</h2>
            {c.summary ? <p className="ar-article-summary">{c.summary}</p> : null}
            {c.hero?.url ? (
              <div className="ar-hero">
                <img src={c.hero.url} alt={c.hero.alt || ''} />
                <span className="ar-hero-chip" data-tone={altMissing ? 'warn' : undefined}>
                  {altMissing ? 'Hero image · alt text missing' : 'Hero image'}
                </span>
              </div>
            ) : null}
            {body ? (
              <div className="ar-article-body" dangerouslySetInnerHTML={{ __html: body }} />
            ) : (
              <div className="ar-dashed">
                Body text is in the editor. Open in editor to read it in full.
              </div>
            )}
          </article>
        )}
      </div>
    </>
  );
}

function SitePreview({
  itemKey,
  spec,
  host,
}: {
  itemKey: string;
  spec: Extract<PreviewSpec, { type: 'site' }>;
  host: string;
}) {
  const { accountId } = useAccount();
  const [createPreview] = useMutation(CREATE_TINYCMS_PREVIEW);
  const cacheKey = `${accountId}:${itemKey}${spec.review ? ':review' : ''}`;
  const [src, setSrc] = useState<string | null>(minted.get(cacheKey) || null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (!accountId) return;
    const known = minted.get(cacheKey);
    if (known) {
      setSrc(known);
      return;
    }
    let live = true;
    setSrc(null);
    setFailed(null);
    createPreview({ variables: { accountId, pageId: spec.pageId || null } })
      .then(({ data }) => {
        const url = data?.createTinycmsPreview?.url;
        if (!url) throw new Error('No preview address came back.');
        const working = spec.review
          ? withReview(workingAddress(url), spec.review)
          : workingAddress(url);
        minted.set(cacheKey, working);
        if (live) setSrc(working);
      })
      .catch((err) => live && setFailed(err instanceof Error ? err.message : 'It couldn’t load.'));
    return () => {
      live = false;
    };
  }, [accountId, cacheKey, spec.pageId, spec.review, createPreview]);

  return (
    <>
      <Bar where={`${host} · ${spec.where}`} legend={Boolean(spec.review)} />
      <div className="ar-pv-body">
        <div className="ar-frame">
          {spec.changes.length && !spec.review ? (
            <div className="ar-changes">
              <span className="ar-frame-label">Proposed</span>
              <div className="ar-changes-title">{spec.changesTitle}</div>
              <ul>
                {spec.changes.map((c, i) => (
                  <li key={`${i}:${c}`}>
                    <span>{i + 1}</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="ar-frame-box">
            {src ? (
              <SitePreviewFrame
                src={src}
                height={640}
                title="The working copy on your site"
                eager
              />
            ) : (
              <div className="ar-pv-loading" style={{ padding: 24 }}>
                {failed ? `The preview couldn’t load: ${failed}` : 'Loading the working copy…'}
              </div>
            )}
          </div>
          {spec.review ? (
            <div className="ar-pv-caption">
              Outlines and tags are only in this review. Visitors see the plain page.
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function PostCard({ post, site }: { post: PostView; site: string }) {
  const url = post.url ? post.url.replace(/^https?:\/\//, '') : null;
  return (
    <div className="ar-post">
      <div className="ar-post-head">
        <span className="ar-post-avatar" aria-hidden="true">
          {initials(site)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="ar-post-site">{site}</div>
          <div className="ar-post-meta">{post.meta}</div>
        </div>
        <span className="ar-post-badge" title={post.network}>
          {post.mark}
        </span>
      </div>
      <div className="ar-post-text">{post.text || 'No caption'}</div>
      {post.image || url ? (
        <div className="ar-post-link">
          {post.image ? (
            <div className="ar-post-link-img">
              {/\.mp4(\?|$)/i.test(post.image) ? (
                <video src={post.image} controls muted playsInline preload="metadata" />
              ) : (
                <img src={post.image} alt="" />
              )}
            </div>
          ) : null}
          {url ? <div className="ar-post-url">{url}</div> : null}
        </div>
      ) : null}
      {post.warn.map((w) => (
        <div key={w} className="ar-post-warn">
          {w}
        </div>
      ))}
    </div>
  );
}

function DatasetPreview({
  spec,
  host,
}: {
  spec: Extract<PreviewSpec, { type: 'dataset' }>;
  host: string;
}) {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_DATASET, {
    variables: { accountId, articleId: spec.articleId, includeDocument: true },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const ds = data?.tinycmsDataset;
  const records: unknown[] = Array.isArray(ds?.document) ? ds.document : [];
  const rows = records
    .slice(0, 8)
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object');
  const cols = rows.length ? Object.keys(rows[0]).slice(0, 4) : [];
  const cell = (v: unknown) =>
    v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return (
    <>
      <Bar where={`${host} · dataset “${spec.name || 'Dataset'}”, working copy`} />
      <div className="ar-pv-body">
        <div className="ar-article" style={{ gap: 14 }}>
          <div className="ar-article-meta">
            <span>Dataset</span>
            <span>·</span>
            <span>{spec.records != null ? `${spec.records} records` : 'Records'}</span>
            {ds?.state?.schemaChanged ? (
              <>
                <span>·</span>
                <span>Schema changed</span>
              </>
            ) : null}
          </div>
          <h2 className="ar-article-title" style={{ fontSize: 26 }}>
            {spec.name || 'Dataset'}
          </h2>
          {!ds ? (
            <div className="ar-pv-loading">{loading ? 'Loading the records…' : ''}</div>
          ) : cols.length ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="ink-table" style={{ width: '100%', fontSize: 13.5 }}>
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th key={c} style={{ textAlign: 'left' }}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      {cols.map((c) => (
                        <td key={c}>{cell(r[c])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {records.length > rows.length ? (
                <div className="ar-help" style={{ marginTop: 8 }}>
                  First {rows.length} of {records.length}. The rest are in the editor.
                </div>
              ) : null}
            </div>
          ) : (
            <div className="ar-dashed">
              The records are in the editor. Open in editor to see them.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function ApprovalPreview({ item, siteName }: { item: ApprovalItem; siteName: string }) {
  const { accountId } = useAccount();
  const { sites } = useTinycmsSites();
  const host = sites.find((s) => s.accountId === accountId)?.host || siteName;
  const spec = item.preview;
  let pane: React.ReactNode;
  if (spec.type === 'article') {
    pane = (
      <ArticlePreview
        key={spec.articleId}
        articleId={spec.articleId}
        host={host}
        check={item.check}
      />
    );
  } else if (spec.type === 'site') {
    pane = <SitePreview key={item.key} itemKey={item.key} spec={spec} host={host} />;
  } else if (spec.type === 'dataset') {
    pane = <DatasetPreview key={spec.articleId} spec={spec} host={host} />;
  } else if (spec.type === 'posts') {
    const n = spec.posts.length;
    pane = (
      <>
        <Bar
          where={`${n > 1 ? `${n} posts` : 'Post'} · ${spec.failed ? 'failed, waiting to retry' : 'waiting to go out'}`}
        />
        <div className="ar-pv-body">
          <div className="ar-posts">
            {spec.posts.map((p) => (
              <PostCard key={p.id} post={p} site={siteName} />
            ))}
          </div>
        </div>
      </>
    );
  } else {
    pane = (
      <>
        <Bar
          where={spec.where}
          pill={spec.type === 'request' ? 'Not approved yet' : 'Not live yet'}
        />
        <div className="ar-pv-body">
          <div className="ar-other">
            {spec.type === 'request' ? (
              <>
                <div className="ar-other-label">{spec.label}</div>
                <div className="ar-other-quote">{spec.quote}</div>
              </>
            ) : (
              <div>{spec.text}</div>
            )}
            {spec.lines?.length ? (
              <ul>
                {spec.lines.map((l, i) => (
                  <li key={`${i}:${l}`}>{l}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </>
    );
  }
  return (
    <section className="ar-preview" aria-label="Preview">
      {pane}
    </section>
  );
}
